import axios from 'axios';
import { auth } from '../firebaseConfig'; // Import our central auth instance
import { cachedGet, invalidateKey } from '../utils/apiCache';

// Create an axios instance with the base URL of our backend API.
// This URL comes from the environment variable we set in AWS Amplify.
const apiClient = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
});

// Token cache — avoids a Firebase network round-trip on every request.
// Refresh 5 minutes before expiry; Firebase tokens are valid for 1 hour.
const TOKEN_REFRESH_BUFFER_MS = 5 * 60 * 1000;
const TOKEN_TTL_MS = 60 * 60 * 1000;
let cachedToken = null;    // { token: string, expiresAt: number }
let tokenPromise = null;   // in-flight promise — prevents concurrent refresh races

const getToken = (user) => {
  if (cachedToken && cachedToken.expiresAt - Date.now() > TOKEN_REFRESH_BUFFER_MS) {
    return Promise.resolve(cachedToken.token);
  }
  if (tokenPromise) return tokenPromise;
  tokenPromise = user.getIdToken().then((token) => {
    cachedToken = { token, expiresAt: Date.now() + TOKEN_TTL_MS };
    tokenPromise = null;
    return token;
  }).catch((err) => {
    tokenPromise = null;
    throw err;
  });
  return tokenPromise;
};

export const clearTokenCache = () => {
  cachedToken = null;
  tokenPromise = null;
};

apiClient.interceptors.request.use(async (config) => {
  const user = auth.currentUser;
  if (user) {
    const token = await getToken(user);
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
}, (error) => {
  return Promise.reject(error);
});

// --- Define functions for our Business endpoints ---

export const getBusinesses = () => cachedGet(apiClient, '/businesses');

export const createBusiness = (businessData) =>
  apiClient.post('/businesses', businessData).then((res) => {
    invalidateKey('/businesses');
    return res;
  });

export const updateBusiness = (businessId, businessData) =>
  apiClient.put(`/businesses/${businessId}`, businessData).then((res) => {
    invalidateKey('/businesses');
    return res;
  });

export const updateBusinessDocumentEligibility = (businessId, payload) =>
  apiClient.put(`/businesses/${businessId}/document-eligibility`, payload);

export const deactivateBusiness = (businessId) =>
  apiClient.put(`/businesses/${businessId}/deactivate`).then((res) => {
    invalidateKey('/businesses');
    return res;
  });

export const activateBusiness = (businessId) =>
  apiClient.put(`/businesses/${businessId}/activate`).then((res) => {
    invalidateKey('/businesses');
    return res;
  });

export const deleteBusiness = (businessId) =>
  apiClient.delete(`/businesses/${businessId}`).then((res) => {
    invalidateKey('/businesses');
    return res;
  });

export const getContacts = (businessId) =>
  cachedGet(apiClient, '/contacts', { businessId });

export const createContact = (contactData) =>
  apiClient.post('/contacts', contactData).then((res) => {
    invalidateKey('/contacts', { businessId: contactData.businessId });
    return res;
  });

export const updateContact = (contactId, contactData) =>
  apiClient.put(`/contacts/${contactId}`, contactData).then((res) => {
    if (contactData.businessId) invalidateKey('/contacts', { businessId: contactData.businessId });
    return res;
  });

export const deleteContact = (contactId, businessId) =>
  apiClient.delete(`/contacts/${contactId}`, { params: { businessId } }).then((res) => {
    invalidateKey('/contacts', { businessId });
    return res;
  });


export const grantAppAccess = (contactId, businessId) => apiClient.post(`/contacts/${contactId}/grant-access`, { businessId });

export const revokeAppAccess = (contactId, businessId) => apiClient.post(`/contacts/${contactId}/revoke-access`, { businessId });

export const requestPasswordReset = (email) => apiClient.post('/auth/password-reset', { email });

export const getBranches = (businessId) =>
  cachedGet(apiClient, '/branches', { businessId });

export const createBranch = (branchData) =>
  apiClient.post('/branches', branchData).then((res) => {
    invalidateKey('/branches', { businessId: branchData.businessId });
    return res;
  });

export const updateBranch = (branchId, branchData) =>
  apiClient.put(`/branches/${branchId}`, branchData).then((res) => {
    if (branchData.businessId) invalidateKey('/branches', { businessId: branchData.businessId });
    return res;
  });

export const deleteBranch = (branchId, businessId) =>
  apiClient.delete(`/branches/${branchId}`, { params: { businessId } }).then((res) => {
    invalidateKey('/branches', { businessId });
    return res;
  });

/** Document Title APIs */
export const getDocumentTitles = () => cachedGet(apiClient, '/document-titles');
export const createDocumentTitle = (payload) =>
  apiClient.post('/document-titles', payload).then((res) => {
    invalidateKey('/document-titles');
    return res;
  });
export const updateDocumentTitle = (documentTitleId, payload) =>
  apiClient.put(`/document-titles/${documentTitleId}`, payload).then((res) => {
    invalidateKey('/document-titles');
    return res;
  });
export const deleteDocumentTitle = (documentTitleId) =>
  apiClient.delete(`/document-titles/${documentTitleId}`).then((res) => {
    invalidateKey('/document-titles');
    return res;
  });

/** Document APIs */
export const getDocuments = (branchId, businessId) => {
  const params = { branchId };
  if (businessId) params.businessId = businessId;
  return apiClient.get('/documents', { params });
  
};

export const createDocument = (payload) => apiClient.post('/documents', payload);

export const updateDocument = (documentId, payload) => apiClient.put(`/documents/${documentId}`, payload);

export const deleteDocument = (documentId, branchId, businessId) => {
  const params = { branchId };
  if (businessId) params.businessId = businessId;
  return apiClient.delete(`/documents/${documentId}`, { params });
};

export const getDocumentUploadUrl = (payload) => apiClient.post('/documents/upload-url', payload);

export const getDocumentDownloadUrl = (documentId, payload) => apiClient.post(`/documents/${documentId}/download-url`, payload);

export const verifyDocument = (documentId, payload) => apiClient.post(`/documents/${documentId}/verify`, payload);

export const notifyExpiringDocuments = () => apiClient.post('/notifications/expiring-documents');

/** Audit APIs */
export const getAudits = (branchId, businessId) =>
  cachedGet(apiClient, '/audits', { branchId, businessId }, 60_000);

export const getAudit = (auditId, branchId, businessId) => apiClient.get(`/audits/${auditId}`, { params: { branchId, businessId } });

export const createAudit = (payload) =>
  apiClient.post('/audits', payload).then((res) => {
    invalidateKey('/audits', { branchId: payload.branchId, businessId: payload.businessId });
    return res;
  });

export const deleteAudit = (auditId, branchId, businessId) =>
  apiClient.delete(`/audits/${auditId}`, { params: { branchId, businessId } }).then((res) => {
    invalidateKey('/audits', { branchId, businessId });
    return res;
  });

export const generateAuditAttachmentUploadUrl = (payload) =>
  apiClient.post('/audits/attachments/upload-url', payload);

export const getAuditAttachmentDownloadUrl = (payload) =>
  apiClient.post('/audits/attachments/download-url', payload);

/** Audit drafts */
export const getAuditDraft = (branchId, businessId) =>
  apiClient.get('/audits/drafts', { params: { branchId, businessId } });

export const saveAuditDraft = (payload) =>
  apiClient.put('/audits/drafts', payload);

export const deleteAuditDraft = (branchId, businessId) =>
  apiClient.delete('/audits/drafts', { params: { branchId, businessId } });

/** Branch Share APIs */
export const createBranchShare = (branchId, payload = {}) =>
  apiClient.post(`/branches/${branchId}/share`, payload);

export const getSharedBranch = (shareId, accessCode) =>
  apiClient.get(`/share/${shareId}`, { params: { accessCode } });

export const getSharedDocumentDownloadUrl = (shareId, documentId, accessCode) =>
  apiClient.post(`/share/${shareId}/documents/${documentId}/download-url`, { accessCode });

/** Report APIs */
export const listReports = (branchId, businessId, reportType) =>
  apiClient.get('/reports', { params: { branchId, businessId, reportType } });

export const getReport = (branchId, businessId, reportType, year, month, day, epochMs) =>
  apiClient.get('/reports/record', { params: { branchId, businessId, reportType, year, month, day, epochMs } });

export const createReport = (payload) => apiClient.post('/reports', payload);

export const updateReport = (payload) => apiClient.put('/reports/record', payload);

export const deleteReport = (branchId, businessId, reportType, year, month, day, epochMs) =>
  apiClient.delete('/reports/record', { params: { branchId, businessId, reportType, year, month, day, epochMs } });

export const verifyReport = (payload) => apiClient.post('/reports/record/verify', payload);

export const resendReport = (payload) => apiClient.post('/reports/record/resend', payload);
